import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import api from '../utils/api';
import { useCRMAccess } from '../utils/crmAccess';
import { onSocket } from '../utils/socket';
import { useMeetingsAccess } from '../utils/meetingsAccess';
import { useAcademyAccess } from '../utils/academyAccess';
import {
    faChartSimple,
    faCommentDots,
    faComments,
    faThLarge,
    faCalendarCheck,
    faUser,
    faFileAlt,
    faCalendarAlt,
    faUsers,
    faShieldAlt,
    faBoxOpen,
    faTimes,
    faUserTimes,
    faFolderOpen,
    faBoxes,
    faRobot,
    faPlug,
    faLaptopHouse,
    faWallet,
    faFileContract,
    faFileInvoiceDollar,
    faClipboardList,
    faChartLine,
    faBell,
    faChevronDown,
    faBriefcase,
    faClock,
    faIdCard,
    faUsersGear,
    faListCheck,
    faCoins,
    faGear,
    faAddressBook,
    faHandshake,
    faWarehouse,
    faEllipsis,
    faCircleQuestion,
    faGraduationCap,
    faVideo
} from '@fortawesome/free-solid-svg-icons';

const OPEN_GROUP_KEY = 'sidebar.openGroup';

const readOpenGroup = () => {
    try { return localStorage.getItem(OPEN_GROUP_KEY); } catch { return null; }
};
const writeOpenGroup = (id) => {
    try {
        if (id) localStorage.setItem(OPEN_GROUP_KEY, id);
        else localStorage.removeItem(OPEN_GROUP_KEY);
    } catch { /* storage unavailable - the group just won't be remembered */ }
};

// An item is active on its own path, plus any `prefixes` - the add/edit/detail
// pages that belong to it, so its group stays open while you are on them.
const isItemActive = (item, pathname) =>
    pathname === item.to || (item.prefixes || []).some(p => pathname.startsWith(p));

const Badge = ({ value, color }) => (
    <span className="nav-badge" style={{ background: color }}>
        {value > 99 ? '99+' : value}
    </span>
);

const Sidebar = ({ isOpen, onClose, onOpen }) => {
    const drawerRef = useRef(null);
    const location = useLocation();
    const user = JSON.parse(localStorage.getItem('user'));
    const userRole = user?.role || 'EMPLOYEE';
    const crmAccess = useCRMAccess(user);
    // Meetings are piloted: the server says who sees them (utils/meetingsAccess.js).
    const { enabled: meetingsEnabled } = useMeetingsAccess();
    // Academy too: the server says who sees it (utils/academyAccess.js).
    const { enabled: academyEnabled } = useAcademyAccess();

    useEffect(() => {
        if (!isOpen || !window.matchMedia('(max-width: 768px)').matches) return;
        const previous = document.activeElement;
        const drawer = drawerRef.current;
        // Focus after the hidden drawer's visibility has been recalculated.
        const focusFrame = requestAnimationFrame(() => drawer.querySelector('button')?.focus());
        const trapFocus = event => {
            if (event.key !== 'Tab') return;
            const items = [...drawer.querySelectorAll('a, button')].filter(el => !el.closest('[inert]') && el.getClientRects().length);
            const first = items[0], last = items[items.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        };
        drawer.addEventListener('keydown', trapFocus);
        return () => { cancelAnimationFrame(focusFrame); drawer.removeEventListener('keydown', trapFocus); previous?.focus(); };
    }, [isOpen]);

    // State to check if the user has inventory assigned
    const [hasInventory, setHasInventory] = useState(false);
    // Count of GTS Documents this user still needs to acknowledge
    const [pendingDocs, setPendingDocs] = useState(0);
    // Count of tasks assigned to this user that aren't finished yet
    const [openTasks, setOpenTasks] = useState(0);
    // Unread chat messages across every conversation this user is in
    const [unreadChats, setUnreadChats] = useState(0);

    useEffect(() => {
        if (userRole === 'EMPLOYEE' || userRole === 'MANAGER') {
            api.get('/inventory/my-items')
                .then(res => {
                    if (res.data && res.data.length > 0) {
                        setHasInventory(true);
                    }
                })
                .catch(err => console.error("Could not check inventory:", err));
        }
    }, [userRole]);

    // My Tasks and GTS Documents, kept live the way the chat badge is: the
    // server says "your tasks / documents changed" (server/utils/countSignals.js)
    // and only the number is fetched again. A burst of changes is one fetch.
    // Coming back to the tab also re-reads them, for anything missed while the
    // connection was down.
    useEffect(() => {
        const loadDocs = () => api.get('/documents/pending-count')
            .then(res => setPendingDocs(res.data.pending || 0))
            .catch(() => { });
        const loadTasks = () => api.get('/tasks/my/open-count')
            .then(res => setOpenTasks(res.data.count || 0))
            .catch(() => { });

        const timers = {};
        const later = (key, load) => () => {
            clearTimeout(timers[key]);
            timers[key] = setTimeout(load, 500);
        };
        const refreshDocs = later('docs', loadDocs);
        const refreshTasks = later('tasks', loadTasks);
        const onVisible = () => {
            if (document.visibilityState === 'visible') { refreshDocs(); refreshTasks(); }
        };

        loadDocs();
        loadTasks();
        const offDocs = onSocket('documents:changed', refreshDocs);
        const offTasks = onSocket('tasks:changed', refreshTasks);
        const offReconnect = onSocket('connect', () => { refreshDocs(); refreshTasks(); });
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            Object.values(timers).forEach(clearTimeout);
            offDocs(); offTasks(); offReconnect();
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    // Unread chat total. Refreshed on every incoming message rather than
    // polled — the socket already tells us the moment one lands, and a timer
    // would either lag behind it or hammer the endpoint for nothing.
    useEffect(() => {
        // Just the count. This used to fetch the whole chat list — every group's
        // member list included — on each page change and each message, which made
        // the whole portal feel slow. A server that predates /unread-total falls
        // back to counting from the list.
        const load = () => api.get('/conversations/unread-total')
            .then(res => setUnreadChats(res.data?.total || 0))
            .catch(err => {
                if (err.response?.status !== 404) return;
                api.get('/conversations', { params: { view: 'summary' } })
                    .then(res => setUnreadChats((res.data || []).reduce((sum, c) => sum + (c.unread || 0), 0)))
                    .catch(() => { });
            });

        // A burst of messages is one refresh, not one per message.
        let timer = null;
        const refresh = () => {
            clearTimeout(timer);
            timer = setTimeout(load, 500);
        };

        load();
        const offActivity = onSocket('conversation:activity', refresh);
        const offRead = onSocket('message:read', refresh);
        return () => { clearTimeout(timer); offActivity(); offRead(); };
    }, [location.pathname]);

    // Vendors: shown to everyone who sees the directory, and to anyone else only
    // once they have brought a vendor in. Re-read on navigation, so inviting the
    // first one makes it appear without a reload.
    const [showVendors, setShowVendors] = useState(['ADMIN', 'HR', 'MANAGER'].includes(userRole));
    useEffect(() => {
        if (['ADMIN', 'HR', 'MANAGER'].includes(userRole)) return;
        api.get('/external-users/summary')
            .then(res => setShowVendors(Boolean(res.data?.show)))
            .catch(() => { });
    }, [userRole, location.pathname]);

    // Auto-close sidebar on mobile after clicking a link
    const handleLinkClick = () => {
        if (window.innerWidth <= 768) {
            onClose();
        }
    };

    // Helper for Management Roles
    const isManagement = ['HR', 'ADMIN', 'MANAGER', 'ACCOUNTS', 'TEAM LEAD'].includes(userRole);
    const isTeamRole = ['MANAGER', 'TEAM LEAD'].includes(userRole);
    // ADMIN has no personal attendance/leave/payroll of their own.
    const hasPersonalHr = userRole !== 'ADMIN';

    // Always on screen: used many times a day, and three carry live counts
    // that would be hidden inside a collapsed group.
    const pinned = [
        { to: '/dashboard', label: 'Dashboard', icon: faThLarge },
        { to: '/chats', label: 'Chats', icon: faComments, prefixes: ['/chats/'], badge: unreadChats, badgeColor: '#128c7e' },
        { to: '/notifications', label: 'Notification Center', icon: faBell },
        { to: '/documents', label: 'GTS Documents', icon: faFileContract, badge: pendingDocs, badgeColor: '#dc2626' },
    ];

    // Each item keeps its own role gate; a group whose items are all hidden
    // for this role is not rendered at all.
    const groups = [
        {
            id: 'work', label: 'My Work', icon: faBriefcase, items: [
                // Every role — anyone can be assigned a task
                { to: '/my-tasks', label: 'My Tasks', icon: faClipboardList, badge: openTasks, badgeColor: '#215D7B' },
                // Every role, including Team Lead: scoped to the projects you are on
                // Pilot only - hidden for everyone the server does not admit.
                { to: '/meetings', label: 'Meetings & Events', icon: faVideo, show: meetingsEnabled },
                { to: '/my-projects', label: 'My Projects', icon: faFolderOpen, prefixes: ['/my-projects/'] },
                { to: '/expenses', label: 'My Expenses', icon: faBoxOpen, prefixes: ['/add-expense', '/edit-expense/'], show: user?.isPurchaser || isManagement },
            ]
        },
        {
            id: 'time', label: 'Time & Leave', icon: faClock, items: [
                { to: '/attendance', label: 'Attendance', icon: faCalendarCheck, show: hasPersonalHr },
                { to: '/leaves', label: 'Leave Management', icon: faFileAlt, show: hasPersonalHr },
                { to: '/wfh', label: 'Work From Home', icon: faLaptopHouse, show: hasPersonalHr },
                { to: '/calendar', label: 'Yearly Calendar', icon: faCalendarAlt, show: hasPersonalHr },
            ]
        },
        {
            id: 'me', label: 'Me', icon: faIdCard, items: [
                { to: '/profile', label: 'My Profile', icon: faUser, show: hasPersonalHr },
                // Deliberately not /payroll: for HR that route is the company
                // register, not their own payslips.
                { to: '/my-payroll', label: 'My Payroll', icon: faFileInvoiceDollar, show: hasPersonalHr },
                { to: '/my-inventory', label: 'My Inventory', icon: faBoxes, show: hasInventory },
                { to: '/academy', label: 'Academy', icon: faGraduationCap, show: academyEnabled },
                // Every role: the onboarding videos and FAQ
                { to: '/help', label: 'Help & Training', icon: faCircleQuestion },
            ]
        },
        {
            id: 'people', label: isTeamRole ? 'Team' : 'People', icon: faUsersGear, items: [
                { to: '/employees', label: isTeamRole ? 'My Team' : 'Employees', icon: faUsers, prefixes: ['/employee/', '/add-employee', '/edit-employee/'], show: isManagement },
                { to: '/Employee-requests', label: isTeamRole ? 'Team Requests' : 'Employee Requests', icon: faFileAlt, show: isManagement },
                { to: '/attendance-logs', label: 'Attendance Logs', icon: faCalendarCheck, prefixes: ['/raw-punches'], show: isManagement && userRole !== 'TEAM LEAD' },
                { to: '/absent-employees', label: 'Live Absence', icon: faUserTimes, show: isManagement },
                // Narrower than Task Report: this one scores people over time,
                // which is review material. Matched by the same ADMIN/HR gate
                // on /api/analytics.
                { to: '/performance', label: 'Performance', icon: faChartSimple, prefixes: ['/performance/'], show: ['ADMIN', 'HR'].includes(userRole) },
            ]
        },
        {
            id: 'manage', label: 'Work Management', icon: faListCheck, items: [
                { to: '/projects', label: 'Projects', icon: faFolderOpen, prefixes: ['/projects/'], show: isManagement && userRole !== 'TEAM LEAD' },
                { to: '/crm', label: 'CRM', icon: faHandshake, prefixes: ['/crm/'], show: crmAccess.enabled },
                // Admin, HR and managers always; anyone else once they manage a
                // vendor (the server decides - see /external-users/summary).
                { to: '/external-users', label: 'Guests', icon: faAddressBook, show: showVendors },
                { to: '/tasks', label: isTeamRole ? 'Assign Tasks' : 'All Tasks', icon: faClipboardList, prefixes: ['/tasks/', '/add-task', '/edit-task/'], show: isManagement },
                // Same roles that can assign tasks (ACCOUNTS is management but
                // was never one of them).
                { to: '/task-report', label: 'Task Report', icon: faChartLine, show: ['MANAGER', 'TEAM LEAD', 'ADMIN', 'HR'].includes(userRole) },
            ]
        },
        {
            id: 'finance', label: 'Finance & Assets', icon: faCoins, items: [
                { to: '/admin-expenses', label: isTeamRole ? 'Team Expenses' : 'All Expenses', icon: faBoxOpen, prefixes: ['/all-expenses'], show: isManagement },
                { to: '/reimbursements', label: 'Reimbursements', icon: faWallet, show: ['HR', 'ADMIN', 'ACCOUNTS'].includes(userRole) },
                { to: '/payroll', label: 'Payroll', icon: faFileInvoiceDollar, show: ['HR', 'ADMIN'].includes(userRole) },
                { to: '/inventory', label: 'Global Inventory', icon: faWarehouse, prefixes: ['/add-inventory', '/edit-inventory/'], show: ['ADMIN', 'HR', 'ACCOUNTS', 'TEAM LEAD'].includes(userRole) },
            ]
        },
        {
            id: 'system', label: 'System & Tools', icon: faGear, items: [
                { to: '/admin-chat', label: 'AI Assistant', icon: faRobot, show: ['HR', 'ADMIN'].includes(userRole) },
                { to: '/admin-settings', label: 'Admin Control', icon: faShieldAlt, show: userRole === 'ADMIN' },
                { to: '/whatsapp', label: 'WhatsApp', icon: faCommentDots, show: userRole === 'ADMIN' },
                { to: '/usage-report', label: 'Usage Report', icon: faChartLine, show: userRole === 'ADMIN' },
                { to: '/integrations', label: 'Integrations', icon: faPlug, show: userRole === 'ADMIN' },
            ]
        },
    ]
        .map(g => ({ ...g, items: g.items.filter(i => i.show === undefined || i.show) }))
        .filter(g => g.items.length > 0);

    const activeGroupId = groups.find(g => g.items.some(i => isItemActive(i, location.pathname)))?.id;

    // Accordion: one group open at a time. Navigating into a group opens it;
    // otherwise the last group the user opened is restored.
    const [openGroup, setOpenGroup] = useState(() => activeGroupId || readOpenGroup());

    useEffect(() => {
        if (activeGroupId) setOpenGroup(activeGroupId);
    }, [activeGroupId]);

    const toggleGroup = (id) => {
        const next = openGroup === id ? null : id;
        setOpenGroup(next);
        writeOpenGroup(next);
    };

    const renderLink = (item, nested = false) => (
        <Link
            key={item.to}
            to={item.to}
            onClick={handleLinkClick}
            className={`nav-link ${nested ? 'nav-sublink' : ''} ${isItemActive(item, location.pathname) ? 'active' : ''}`}
        >
            <FontAwesomeIcon icon={item.icon} className="nav-icon" /> <span>{item.label}</span>
            {item.badge > 0 && <Badge value={item.badge} color={item.badgeColor} />}
        </Link>
    );

    const mobileTabs = [
        { to: '/dashboard', label: 'Dashboard', icon: faThLarge },
        ...(hasPersonalHr ? [{ to: '/attendance', label: 'Attendance', icon: faCalendarCheck }] : []),
        // Admin gets the Task Report here instead; their own tasks stay under My Work.
        userRole === 'ADMIN'
            ? { to: '/task-report', label: 'Task Report', icon: faChartLine }
            : { to: '/my-tasks', label: 'Tasks', icon: faClipboardList, badge: openTasks },
        { to: '/chats', label: 'Chats', icon: faComments, prefixes: ['/chats/'], badge: unreadChats },
    ];
    const moreActive = !mobileTabs.some(item => isItemActive(item, location.pathname));

    return (
        <>
        <div id="app-sidebar" ref={drawerRef} className={`sidebar ${isOpen ? 'mobile-open' : ''}`}>
            {/* Close button visible only on mobile drawer */}
            <div className="mobile-only-header">
                <span className="mobile-menu-title">More</span>
                <button className="close-sidebar-btn" onClick={onClose} aria-label="Close navigation">
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            <div className="role-badge">
                {userRole} MODE
            </div>

            <nav className="sidebar-menu">
                {pinned.map(item => renderLink(item))}

                {groups.map(group => {
                    const expanded = openGroup === group.id;
                    const isActive = group.id === activeGroupId;
                    // A collapsed group carries its children's counts, so a
                    // badge never disappears just because its group is closed.
                    const collapsedBadge = group.items.reduce((sum, i) => sum + (i.badge || 0), 0);
                    const badgeColor = group.items.find(i => i.badge > 0)?.badgeColor;

                    return (
                        <div key={group.id} className={`nav-group ${expanded ? 'expanded' : ''}`}>
                            <button
                                type="button"
                                className={`nav-link nav-group-toggle ${isActive ? 'has-active' : ''}`}
                                onClick={() => toggleGroup(group.id)}
                                aria-expanded={expanded}
                                aria-controls={`nav-group-${group.id}`}
                            >
                                <FontAwesomeIcon icon={group.icon} className="nav-icon" /> <span>{group.label}</span>
                                {!expanded && collapsedBadge > 0 && <Badge value={collapsedBadge} color={badgeColor} />}
                                <FontAwesomeIcon icon={faChevronDown} className="nav-group-chevron" />
                            </button>
                            {/* Always rendered so the open/close can animate;
                                inert keeps a closed group's links out of the
                                tab order and away from screen readers. */}
                            <div
                                id={`nav-group-${group.id}`}
                                className="nav-group-items"
                                inert={expanded ? undefined : ''}
                            >
                                <div className="nav-group-items-inner">
                                    {group.items.map((item, i) => (
                                        <div key={item.to} className="nav-group-item" style={{ '--i': i }}>
                                            {renderLink(item, true)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </nav>
        </div>
        <nav className="mobile-tabbar" aria-label="Main navigation">
            {mobileTabs.map(item => <Link key={item.to} to={item.to} onClick={onClose}
                className={`mobile-tab ${!isOpen && isItemActive(item, location.pathname) ? 'active' : ''}`}
                aria-current={!isOpen && isItemActive(item, location.pathname) ? 'page' : undefined}>
                <span className="mobile-tab-icon"><FontAwesomeIcon icon={item.icon} />
                    {item.badge > 0 && <span className="mobile-tab-badge">{item.badge > 99 ? '99+' : item.badge}</span>}
                </span>
                <span>{item.label}</span>
            </Link>)}
            <button type="button" className={`mobile-tab ${isOpen || moreActive ? 'active' : ''}`}
                onClick={isOpen ? onClose : onOpen} aria-expanded={isOpen} aria-controls="app-sidebar">
                <span className="mobile-tab-icon"><FontAwesomeIcon icon={faEllipsis} /></span><span>More</span>
            </button>
        </nav>
        </>
    );
};

export default Sidebar;
